/* eslint-disable react/jsx-no-useless-fragment */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import {
  addMonths,
  addYears,
  eachMonthOfInterval,
  eachYearOfInterval,
} from 'date-fns';
import {
  MdExpandMore,
  MdOutlineCalendarToday,
  MdPendingActions,
  MdTimer,
} from 'react-icons/md';
import { TbCalendarTime } from 'react-icons/tb';

import { Alert } from '../../../../components/singleComponents/alerts/Alert';

import api, { authHeader } from '../../../../../services/API/apiConfig';

import {
  AccordionDetailsStyled,
  AccordionDivStyled,
  CellStepsStyled,
  DivNoDataStyled,
  FatherTableCellFixedStyled,
  ItemNumStyled,
  ItemStyled,
  LinearProgressStyled,
  LoadingProgressStyled,
  MobileStyled,
  ProjectStepsStyled,
  TableCellFixedStyled,
  TableCellHeaderFixedStyled,
  TableCellHeaderFixedTotalStyled,
  TableCellHeaderStyled,
  TableCellHeaderWithStickyStyled,
  TableCellStyled,
  TableContainerStyled,
  TableRowStyled,
  TitleHeaderStyled,
  TitleStyled,
  ValuesStyled,
} from './ConstructionSchedule.styles';

const monthsOptions = [
  'JAN',
  'FEV',
  'MAR',
  'ABR',
  'MAI',
  'JUN',
  'JUL',
  'AGO',
  'SET',
  'OUT',
  'NOV',
  'DEZ',
];

export function ConstructionSchedules() {
  const [disableSchedule, setDisableSchedule] = useState(false);
  const [constructionData, setConstructionData] = useState();
  const [tableData, setTableData] = useState({});
  const [frequency, setFrequency] = useState('');
  const [frequencyToPortuguese, setFrequencyToPortuguese] = useState('');
  const [periods, setPeriods] = useState(0);
  const [initialDate, setInitialDate] = useState('');
  const [hasSchedule, setHasSchedule] = useState(false);
  const [totalPercentage, setTotalPercentage] = useState([]);
  const [percentageValues, setPercentageValues] = useState([]);
  const [headerData, setHeaderData] = useState([]);
  const headerFrequency = headerData.slice(0, periods);
  const [loading, setLoading] = useState(true);

  const periodsArr = [];
  for (let i = 0; i < periods; i += 1) {
    periodsArr.push(i + 1);
  }

  const { id } = useParams();

  const getHeaderFrequency = () => {
    const convertedDate = initialDate.split('/').reverse().join('/');
    const date = new Date(convertedDate);
    const periodsNum = periods === 0 ? periods : periods - 1;

    const newHeader = [...headerData];

    if (frequency === 'weekly') {
      setFrequencyToPortuguese('semanal');
      periodsArr.forEach((_data, i) => {
        newHeader[i] = `SEMANA ${i + 1}`;
      });
    } else if (frequency === 'biweekly') {
      setFrequencyToPortuguese('quinzenal');
      const periodsIteration = periodsNum / 2 + 1;
      const endBiWeek = addMonths(date, periodsIteration);
      const biWeekArray = eachMonthOfInterval({ start: date, end: endBiWeek });
      const tempArr = [];

      biWeekArray.forEach((mon) => {
        tempArr.push(`${monthsOptions[mon.getMonth()]} 1/2`);
        tempArr.push(`${monthsOptions[mon.getMonth()]} 2/2`);
      });
      if (date.getDate() > 15) {
        tempArr.shift();
      }
      periodsArr.forEach((data, i) => {
        newHeader[i] = tempArr[i];
      });
    } else if (frequency === 'monthly') {
      setFrequencyToPortuguese('mensal');
      const endMonth = addMonths(date, periodsNum);
      const monthsArray = eachMonthOfInterval({ start: date, end: endMonth });

      monthsArray.forEach((mon, idx) => {
        newHeader[idx] = `${
          monthsOptions[mon.getMonth()]
        }/${mon.getFullYear()}`;
      });
    } else if (frequency === 'quarterly') {
      setFrequencyToPortuguese('trimestral');
      const endQuarterly = addMonths(date, periods * 3);
      const quarterlyArray = eachMonthOfInterval({
        start: date,
        end: endQuarterly,
      });
      const quarters = [];
      const totalMonths = [];

      quarterlyArray.forEach((data, i) => {
        totalMonths.push(monthsOptions[quarterlyArray[i].getMonth()]);
      });
      for (let b = 0; b < totalMonths.length; b += 3) {
        const chunk = totalMonths.slice(b, b + 3);
        quarters.push(chunk);
      }
      quarters.forEach((data, i) => {
        newHeader[i] = data.join('/');
      });
    } else if (frequency === 'annualy') {
      setFrequencyToPortuguese('anual');
      const endYears = addYears(date, periodsNum);
      const yearsArray = eachYearOfInterval({ start: date, end: endYears });

      yearsArray.forEach((data, i) => {
        newHeader[i] = data.getFullYear();
      });
    }
    setHeaderData(newHeader);
  };

  const getDataCallback = useCallback(async () => {
    await api
      .get(`/constructions/${id}/details?budget=false`, {
        headers: authHeader(),
      })
      .then((res) => {
        const { data } = res.data;
        setConstructionData(data);
      });

    await api
      .get(`/constructions/${id}/schedules`, {
        headers: authHeader(),
      })
      .then((res) => {
        const { data } = res.data;
        if (data.id) {
          setHasSchedule(true);
          setTableData(data);
          setInitialDate(data.starts_at);
          setFrequency(data.frequency);
          setPeriods(data.periods);
          setLoading(false);

          const savedItems = [...percentageValues];
          const newPercentage = [...totalPercentage];
          data.schedule_items?.forEach((row, i) => {
            savedItems.push([]);
            row.schedule_data?.forEach((cell, idx) => {
              savedItems[i][idx] = cell?.percentage || 0;
            });
            newPercentage[i] = savedItems[i].reduce((p, c) => p + c, 0);
          });

          const percentages = data.schedule_items.map((entry) => {
            const rowValues = new Array(data.periods).fill(0);
            entry.schedule_data.forEach((value) => {
              rowValues[value.period - 1] = value.percentage;
            });
            return rowValues;
          });
          setPercentageValues(percentages);
          setTotalPercentage(newPercentage);
        } else if (!data.length) {
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
        setDisableSchedule(true);
      });
  }, []);

  const childStyle = (num) => num.includes('.');

  const checkTotalPercentage = (idxRow) => {
    if (totalPercentage.length < 1) {
      return '';
    }

    if (
      totalPercentage[idxRow] !== undefined &&
      totalPercentage[idxRow] === 100
    ) {
      return (
        <span style={{ color: '#508239', fontWeight: 600 }}>
          {`${totalPercentage[idxRow]} %`}
        </span>
      );
    }

    if (
      totalPercentage[idxRow] !== undefined &&
      totalPercentage[idxRow] > 100
    ) {
      return (
        <span style={{ color: '#f51f0e', fontWeight: 600 }}>
          {`${totalPercentage[idxRow]} %`}
        </span>
      );
    }

    if (totalPercentage[idxRow] !== undefined && totalPercentage[idxRow] > 0) {
      return (
        <span style={{ color: '#eb7d36', fontWeight: 600 }}>
          {`${totalPercentage[idxRow]} %`}
        </span>
      );
    }

    return '';
  };

  const checkTotalPercentageMobile = (idxRow) => {
    if (totalPercentage.length < 1) {
      return '';
    }

    if (
      totalPercentage[idxRow] !== undefined &&
      totalPercentage[idxRow] === 100
    ) {
      return (
        <span style={{ color: '#00285A', fontSize: 11 }}>
          {`${totalPercentage[idxRow]} %`}
        </span>
      );
    }

    if (
      totalPercentage[idxRow] !== undefined &&
      totalPercentage[idxRow] > 100
    ) {
      return (
        <span style={{ color: '#f51f0e', fontSize: 11 }}>
          {`${totalPercentage[idxRow]} %`}
        </span>
      );
    }

    if (totalPercentage[idxRow] !== undefined && totalPercentage[idxRow] > 0) {
      return (
        <span style={{ color: '#eb7d36', fontSize: 11 }}>
          {`${totalPercentage[idxRow]} %`}
        </span>
      );
    }

    return '';
  };

  useEffect(() => {
    getDataCallback();
  }, [getDataCallback]);

  useEffect(() => {
    if (initialDate !== '') {
      getHeaderFrequency();
    }
  }, [periods, frequency, initialDate]);

  if (loading) {
    return (
      <LoadingProgressStyled>
        <Typography variant="h2">Carregando...</Typography>
        <LinearProgress style={{ width: '80%' }} />
      </LoadingProgressStyled>
    );
  }

  return (
    <>
      {!disableSchedule ? (
        <>
          {hasSchedule ? (
            <>
              <TableContainerStyled>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCellHeaderWithStickyStyled>
                        ETAPAS DO PROJETO
                      </TableCellHeaderWithStickyStyled>
                      <TableCellHeaderFixedTotalStyled>
                        %
                      </TableCellHeaderFixedTotalStyled>
                      {headerFrequency.map((data) => (
                        <TableCellHeaderStyled key={data}>
                          {data}
                        </TableCellHeaderStyled>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.schedule_items &&
                      tableData.schedule_items.map((row, idxRow) => {
                        if (percentageValues.length < 1) {
                          for (
                            let a = 0;
                            a < tableData.schedule_items.length;
                            a += 1
                          ) {
                            percentageValues.push([]);
                          }
                        }

                        return (
                          <TableRowStyled key={row.id}>
                            <CellStepsStyled>
                              <ItemNumStyled>{row.item_num}</ItemNumStyled>
                              {row.description}
                            </CellStepsStyled>
                            <FatherTableCellFixedStyled>
                              {checkTotalPercentage(idxRow)}
                            </FatherTableCellFixedStyled>

                            {periodsArr.map((cell, i) => {
                              if (percentageValues[idxRow].length < 1) {
                                for (let b = 0; b < periods; b += 1) {
                                  percentageValues[idxRow].push(0);
                                }
                              }

                              return childStyle(row.item_num) ? (
                                <TableCellStyled key={cell}>
                                  {row.is_leaf_node && (
                                    <>
                                      <Typography>
                                        {percentageValues[idxRow].length > 0
                                          ? percentageValues[idxRow][i]
                                          : ''}{' '}
                                        %
                                      </Typography>
                                      <LinearProgressStyled
                                        variant="determinate"
                                        value={
                                          percentageValues[idxRow].length > 0
                                            ? percentageValues[idxRow][i]
                                            : 0
                                        }
                                      />
                                    </>
                                  )}
                                </TableCellStyled>
                              ) : (
                                <TableCellFixedStyled key={cell}>
                                  {row.is_leaf_node && (
                                    <>
                                      <Typography>
                                        {percentageValues[idxRow].length > 0
                                          ? percentageValues[idxRow][i]
                                          : ''}{' '}
                                        %
                                      </Typography>
                                      <LinearProgressStyled
                                        variant="determinate"
                                        value={
                                          percentageValues[idxRow].length > 0
                                            ? percentageValues[idxRow][i]
                                            : 0
                                        }
                                      />
                                    </>
                                  )}
                                </TableCellFixedStyled>
                              );
                            })}
                          </TableRowStyled>
                        );
                      })}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCellHeaderFixedStyled colSpan={2}>
                        ORÇAMENTO DO PERÍODO
                      </TableCellHeaderFixedStyled>
                      {tableData.budget_per_period?.map((budget) => (
                        <TableCellHeaderStyled key={budget.period}>
                          R$
                          {budget.total}
                        </TableCellHeaderStyled>
                      ))}
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainerStyled>
              <MobileStyled>
                <TitleStyled variant="h2">Cronograma</TitleStyled>
                <Typography variant="h4">Detalhes do cronograma</Typography>
                <Typography variant="body2" color="primary">
                  Nome da obra
                </Typography>
                <TitleHeaderStyled>
                  <ValuesStyled>
                    <Typography variant="body1">
                      {constructionData.square_meters}
                      m²
                    </Typography>
                    <div className="secondaryColumn">
                      <Typography>
                        <MdOutlineCalendarToday />
                        {` ${initialDate}`}
                      </Typography>
                    </div>
                  </ValuesStyled>
                  <ValuesStyled>
                    <div>
                      <Typography variant="body1">
                        {'Total orçado: '}
                      </Typography>
                      <Typography variant="body1">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }).format(constructionData.total_budget)}
                      </Typography>
                    </div>
                    <div className="secondaryColumn">
                      <Typography>
                        <TbCalendarTime />
                        {frequencyToPortuguese}
                      </Typography>
                    </div>
                  </ValuesStyled>
                  <ValuesStyled>
                    <div>
                      <Typography variant="body1">
                        {'Total/ m² orçado: '}
                      </Typography>
                      <Typography variant="body1">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }).format(constructionData.total_budget_per_m2)}
                      </Typography>
                    </div>
                    <div className="secondaryColumn">
                      <Typography>
                        <MdTimer />
                        {`${tableData.periods} períodos`}
                      </Typography>
                    </div>
                  </ValuesStyled>
                </TitleHeaderStyled>
                <AccordionDivStyled>
                  <Accordion>
                    <AccordionSummary expandIcon={<MdExpandMore />}>
                      <ProjectStepsStyled>
                        <div className="description">
                          <Typography variant="body2">
                            ETAPAS DO PROJETO
                          </Typography>
                          <Typography style={{ fontSize: 10 }}>
                            ver detalhes
                          </Typography>
                        </div>
                        <Typography className="conclusion">
                          conclusão:
                          <Typography component="span" color="primary">
                            100%
                          </Typography>
                        </Typography>
                      </ProjectStepsStyled>
                    </AccordionSummary>
                    {tableData.schedule_items &&
                      tableData.schedule_items.map((item, idxRow) => (
                        <AccordionDetails key={item.id}>
                          <ItemStyled>
                            {item.is_leaf_node ? (
                              <div className="secondaryItem">
                                <div>
                                  <Typography>
                                    {`${item.item_num}. ${item.description}`}
                                  </Typography>
                                </div>
                                <div>
                                  <Typography>Evolução:</Typography>
                                  <Typography>
                                    {checkTotalPercentageMobile(idxRow)}
                                  </Typography>
                                </div>
                              </div>
                            ) : (
                              <div className="primaryItem">
                                <Typography>
                                  {`${item.item_num}. ${item.description}`}
                                </Typography>
                              </div>
                            )}
                          </ItemStyled>
                        </AccordionDetails>
                      ))}
                  </Accordion>
                  <Accordion style={{ marginTop: 8 }}>
                    <AccordionSummary expandIcon={<MdExpandMore />}>
                      <ProjectStepsStyled>
                        <div className="description">
                          <Typography variant="body2">
                            ORÇAMENTO PERÍODO
                          </Typography>
                          <Typography style={{ fontSize: 10 }}>
                            ver detalhes
                          </Typography>
                        </div>
                        <Typography className="conclusion">
                          total orçado:
                          <Typography component="span" color="primary">
                            {new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            }).format(constructionData.total_budget)}
                          </Typography>
                        </Typography>
                      </ProjectStepsStyled>
                    </AccordionSummary>
                    <AccordionDetailsStyled>
                      <div>
                        {headerFrequency.map((header) => (
                          <Typography key={header} style={{ marginLeft: 17 }}>
                            {header.toLowerCase()}
                          </Typography>
                        ))}
                      </div>
                      <div>
                        {tableData.budget_per_period &&
                          tableData.budget_per_period.map((budgetPerPeriod) => (
                            <Typography key={budgetPerPeriod.id}>
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              }).format(budgetPerPeriod.total)}
                            </Typography>
                          ))}
                      </div>
                    </AccordionDetailsStyled>
                  </Accordion>
                </AccordionDivStyled>
              </MobileStyled>
            </>
          ) : (
            <DivNoDataStyled>
              <MdPendingActions size={200} color="#B9B9B9" />
              <Typography variant="body1" style={{ color: '#1c1c1ccc' }}>
                Nenhum cronograma adicionado até o momento.
              </Typography>
            </DivNoDataStyled>
          )}
        </>
      ) : (
        <Alert alertTitle="Cronograma indisponível" />
      )}
    </>
  );
}
